import XLSXStyle from "xlsx-js-style";
import { ProductProps } from "../interfaces/interfaces";

const NAVY = "1F3864";
const GOLD = "FBAB35";
const WHITE = "FFFFFF";
const BLACK = "000000";
const LIGHT_GRAY = "F2F2F2";
const ZERO_AS_DASH_NUMBER_FORMAT = '[=0]"-";General';

const headerStyle = {
  fill: { patternType: "solid", fgColor: { rgb: NAVY } },
  font: { bold: true, color: { rgb: WHITE }, sz: 9 },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: {
    top: { style: "thin", color: { rgb: BLACK } },
    bottom: { style: "thin", color: { rgb: BLACK } },
    left: { style: "thin", color: { rgb: BLACK } },
    right: { style: "thin", color: { rgb: BLACK } },
  },
};

const cellStyle = {
  font: { sz: 9, color: { rgb: BLACK } },
  alignment: { horizontal: "center", vertical: "center" },
  border: {
    top: { style: "thin", color: { rgb: "CCCCCC" } },
    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
    left: { style: "thin", color: { rgb: "CCCCCC" } },
    right: { style: "thin", color: { rgb: "CCCCCC" } },
  },
};

const cellStyleLeft = {
  ...cellStyle,
  alignment: { horizontal: "left", vertical: "center" },
};

const altRowStyle = {
  ...cellStyle,
  fill: { patternType: "solid", fgColor: { rgb: LIGHT_GRAY } },
};

const altRowStyleLeft = {
  ...cellStyleLeft,
  fill: { patternType: "solid", fgColor: { rgb: LIGHT_GRAY } },
};

const mudWeightRowStyle = {
  font: { sz: 9, color: { rgb: BLACK } },
  alignment: { horizontal: "left", vertical: "center" },
  border: {
    top: { style: "thin", color: { rgb: "CCCCCC" } },
    bottom: { style: "thin", color: { rgb: "CCCCCC" } },
    left: { style: "thin", color: { rgb: "CCCCCC" } },
    right: { style: "thin", color: { rgb: "CCCCCC" } },
  },
};

const totalRowStyle = {
  fill: { patternType: "solid", fgColor: { rgb: NAVY } },
  font: { bold: true, color: { rgb: WHITE }, sz: 9 },
  alignment: { horizontal: "center", vertical: "center" },
  border: {
    top: { style: "thin", color: { rgb: BLACK } },
    bottom: { style: "thin", color: { rgb: BLACK } },
    left: { style: "thin", color: { rgb: BLACK } },
    right: { style: "thin", color: { rgb: BLACK } },
  },
};

const goldLabelStyle = {
  fill: { patternType: "solid", fgColor: { rgb: GOLD } },
  font: { bold: true, color: { rgb: BLACK }, sz: 9 },
  alignment: { horizontal: "center", vertical: "center" },
  border: {
    top: { style: "thin", color: { rgb: BLACK } },
    bottom: { style: "thin", color: { rgb: BLACK } },
    left: { style: "thin", color: { rgb: BLACK } },
    right: { style: "thin", color: { rgb: BLACK } },
  },
};

const goldValueStyle = {
  fill: { patternType: "solid", fgColor: { rgb: GOLD } },
  font: { bold: true, color: { rgb: BLACK }, sz: 9 },
  alignment: { horizontal: "center", vertical: "center" },
  border: {
    top: { style: "thin", color: { rgb: BLACK } },
    bottom: { style: "thin", color: { rgb: BLACK } },
    left: { style: "thin", color: { rgb: BLACK } },
    right: { style: "thin", color: { rgb: BLACK } },
  },
};

function cell(value: any, style: any) {
  return { v: value ?? "", t: "s", s: style };
}

function toExcelNumber(value: any): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const normalized = value.trim().replace(/,/g, "");
    if (!normalized) return null;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function numericCell(value: any, style: any, fallback: any = "-") {
  const numberValue = toExcelNumber(value);

  if (numberValue === null) {
    return cell(value ?? fallback, style);
  }

  return {
    v: numberValue,
    t: "n",
    z: ZERO_AS_DASH_NUMBER_FORMAT,
    s: style,
  };
}

function emptyCell() {
  return { v: "", s: cellStyle };
}

function emptyTotalCell() {
  return { v: "", s: totalRowStyle };
}

const COLS = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K"];

function setRow(ws: any, rowIdx: number, values: any[]) {
  COLS.forEach((col, i) => {
    ws[`${col}${rowIdx}`] = values[i] ?? emptyCell();
  });
}

export function exportTallyXlsx(params: {
  tallyDesign: ProductProps[];
  mudWeight: number;
  totalLength: number;
  totalWeight: number;
  tol: number | null | undefined;
  fileName: string;
}) {
  const { tallyDesign, mudWeight, totalLength, totalWeight, tol, fileName } =
    params;

  const wb = XLSXStyle.utils.book_new();
  const ws: any = {};

  // Row 1: header
  const headers = [
    "Description",
    "Supplier",
    "Top Thread Connection",
    "Bottom Thread Connection",
    "Status",
    "Max. OD (in)",
    "Body OD (in)",
    "Length (ft)",
    "Top (ft)",
    "Bottom (ft)",
    "Weight (lb)",
  ];
  setRow(
    ws,
    1,
    headers.map((h) => cell(h, headerStyle)),
  );

  // Data rows
  const items = tallyDesign.filter((item) => item.id !== 0);
  items.forEach((item, idx) => {
    const rowIdx = idx + 2;
    const isAlt = idx % 2 === 1;
    const base = isAlt ? altRowStyleLeft : cellStyleLeft;
    const num = isAlt ? altRowStyle : cellStyle;
    const length =
      item.length && item.quantity ? item.length * item.quantity : item.length;
    const weight =
      item.quantity && item.weight
        ? item.weight * item.quantity
        : item.weight ?? null;

    setRow(ws, rowIdx, [
      cell(item.name || item.description || "", base),
      cell(item.supplier || "-", num),
      cell(item.topThreadConnection || "-", num),
      cell(item.bottomThreadConnection || "-", num),
      cell(item.status?.name || "-", num),
      numericCell(item.maxOD, num),
      numericCell(item.bodyOD, num),
      numericCell(length, num),
      numericCell(item.top, num),
      numericCell(item.bottom, num),
      numericCell(weight, num),
    ]);
  });

  const lastDataRow = items.length + 2;

  // Mud weight row
  setRow(ws, lastDataRow, [
    cell(
      "Weight of sand in the mud joints (filled completely)",
      mudWeightRowStyle,
    ),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    emptyCell(),
    numericCell(mudWeight, cellStyle),
  ]);

  // Merges (mud weight label spans A:J)
  const merges: any[] = [
    {
      s: { r: lastDataRow - 1, c: 0 },
      e: { r: lastDataRow - 1, c: 9 },
    },
  ];

  // Total row
  const totalRow = lastDataRow + 1;
  setRow(ws, totalRow, [
    cell("TOTAL", totalRowStyle),
    emptyTotalCell(),
    emptyTotalCell(),
    emptyTotalCell(),
    emptyTotalCell(),
    emptyTotalCell(),
    emptyTotalCell(),
    numericCell(totalLength, totalRowStyle),
    emptyTotalCell(),
    emptyTotalCell(),
    numericCell(totalWeight, totalRowStyle),
  ]);

  // Merge TOTAL label A:G
  merges.push({
    s: { r: totalRow - 1, c: 0 },
    e: { r: totalRow - 1, c: 6 },
  });

  // TOL / NoGo-TOL rows — label in col I (Top ft), value in col J (Bottom ft)
  const tolRow = totalRow + 1;
  const noGoRow = totalRow + 2;
  ws[`I${tolRow}`] = cell("TOL", goldLabelStyle);
  ws[`J${tolRow}`] = numericCell(tol, goldValueStyle, "");
  ws[`I${noGoRow}`] = cell("NoGo-TOL", goldLabelStyle);
  ws[`J${noGoRow}`] = numericCell(tol, goldValueStyle, "");

  // Column widths
  ws["!cols"] = [
    { wch: 40 }, // Description
    { wch: 14 }, // Supplier
    { wch: 18 }, // Top Thread
    { wch: 18 }, // Bottom Thread
    { wch: 10 }, // Status
    { wch: 11 }, // Max OD
    { wch: 11 }, // Body OD
    { wch: 11 }, // Length
    { wch: 10 }, // Top
    { wch: 10 }, // Bottom
    { wch: 11 }, // Weight
  ];

  ws["!rows"] = [{ hpt: 30 }]; // taller header row

  ws["!merges"] = merges;

  ws["!ref"] = `A1:K${noGoRow}`;

  XLSXStyle.utils.book_append_sheet(wb, ws, "Tally Design");
  XLSXStyle.writeFile(wb, `${fileName}.xlsx`);
}

export function exportTallyCsv(params: {
  tallyDesign: ProductProps[];
  mudWeight: number;
  totalLength: number;
  totalWeight: number;
  tol: number | null | undefined;
  fileName: string;
}) {
  const { tallyDesign, mudWeight, totalLength, totalWeight, tol, fileName } =
    params;

  const escape = (v: any) => {
    const s = v == null ? "" : String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const rows: string[][] = [];
  rows.push([
    "Description",
    "Supplier",
    "Top Thread Connection",
    "Bottom Thread Connection",
    "Status",
    "Max. OD (in)",
    "Body OD (in)",
    "Length (ft)",
    "Top (ft)",
    "Bottom (ft)",
    "Weight (lb)",
  ]);

  tallyDesign
    .filter((item) => item.id !== 0)
    .forEach((item) => {
      const length =
        item.length && item.quantity
          ? item.length * item.quantity
          : item.length;
      const weight: number | null =
        item.quantity && item.weight
          ? item.weight * item.quantity
          : item.weight ?? null;
      rows.push([
        item.name || item.description || "",
        item.supplier || "-",
        item.topThreadConnection || "-",
        item.bottomThreadConnection || "-",
        item.status?.name || "-",
        item.maxOD != null ? String(item.maxOD) : "-",
        item.bodyOD != null ? String(item.bodyOD) : "-",
        length != null ? String(length) : "-",
        item.top != null ? String(item.top) : "-",
        item.bottom != null ? String(item.bottom) : "-",
        weight != null ? String(weight) : "-",
      ]);
    });

  rows.push([
    "Weight of sand in the mud joints (filled completely)",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    String(mudWeight),
  ]);
  rows.push([
    "TOTAL",
    "",
    "",
    "",
    "",
    "",
    "",
    String(totalLength),
    "",
    "",
    String(totalWeight),
  ]);

  rows.push(["", "", "", "", "", "", "", "", "TOL", tol != null ? String(tol) : "", ""]);
  rows.push(["", "", "", "", "", "", "", "", "NoGo-TOL", tol != null ? String(tol) : "", ""]);

  const csv = rows.map((r) => r.map(escape).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
